import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  FaIdCard, FaTrash, FaSearch, FaMapMarkerAlt, FaEnvelope, FaPhone, FaLink,
  FaWallet, FaRedo, FaChevronLeft, FaChevronRight, FaTimes, FaGlobe, FaCheckCircle, FaExclamationTriangle
} from "react-icons/fa";
import { toast } from "react-toastify";

const TotalReporter = () => {
  const [verifiedCards, setVerifiedCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reporterToDelete, setReporterToDelete] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  const fetchVerifiedCards = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/get/approved/icards`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const cardsData = response.data.reportersIdCard || response.data.data || response.data || [];

      if (Array.isArray(cardsData)) {
        setVerifiedCards(cardsData);
      } else {
        setVerifiedCards([]);
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.message ||
        "Failed to fetch verified ID cards"
      );
      setVerifiedCards([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVerifiedCards();
  }, []);

  // Filter reporters by search term
  const filteredCards = verifiedCards.filter((reporter) => {
    const term = searchTerm.toLowerCase();
    return (
      (reporter.name && reporter.name.toLowerCase().includes(term)) ||
      (reporter.email && reporter.email.toLowerCase().includes(term)) ||
      (reporter.mobileNo && reporter.mobileNo.toString().toLowerCase().includes(term)) ||
      (reporter.city && reporter.city.toLowerCase().includes(term)) ||
      (reporter.state && reporter.state.toLowerCase().includes(term)) ||
      (reporter.iinsafId && reporter.iinsafId.toLowerCase().includes(term))
    );
  }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  // Pagination Logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredCards.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredCards.length / itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleDeleteReporter = async (reporterId) => {
    try {
      setDeleteLoading(reporterId);

      const response = await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/admin/delete/reporter-id-card/${reporterId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.success) {
        toast.success(`Reporter ID card deleted successfully`);
        setVerifiedCards(prev => prev.filter(reporter => reporter._id !== reporterId));
        setShowDeleteModal(false);
        setReporterToDelete(null);
      } else {
        toast.error(response.data.message || "Failed to delete reporter ID card");
      }
    } catch (err) {
      console.error("Error deleting reporter:", err);
      toast.error(
        err.response?.data?.message ||
        err.message ||
        "Failed to delete reporter ID card"
      );
    } finally {
      setDeleteLoading(null);
    }
  };

  const showDeleteConfirmation = (reporter) => {
    setReporterToDelete(reporter);
    setShowDeleteModal(true);
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] p-4 md:p-8 font-sans transition-all duration-300">

      {/* Background Decorative Gradient Bar */}
      <div className="fixed top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 z-50"></div>

      {/* Page Header - Compact */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center text-white text-lg shadow-lg shadow-purple-500/20">
            <FaIdCard />
          </div>
          <div>
            <h1 className="text-xl font-black text-[#1e293b] tracking-tight truncate max-w-[200px] md:max-w-none">
              Verified Reporters
            </h1>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="flex items-center gap-1 px-1.5 py-0.5 bg-green-50 text-green-600 rounded-md text-[9px] font-black uppercase tracking-widest border border-green-100">
                <FaCheckCircle size={8} /> Official
              </span>
              <span className="text-[#94a3b8] text-[10px] font-bold uppercase tracking-tighter">
                {verifiedCards.length} Active
              </span>
            </div>
          </div>
        </div>
        <button
          onClick={fetchVerifiedCards}
          className="flex items-center gap-2 px-4 py-2 bg-white text-[#475569] border border-[#e2e8f0] rounded-xl shadow-sm hover:bg-gray-50 transition-all font-bold text-xs"
        >
          <FaRedo className={`${loading ? 'animate-spin' : ''}`} />
          Sync
        </button>
      </div>

      {/* Modern Table Container */}
      <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-purple-500/5 border border-[#f1f5f9] overflow-hidden">

        {/* Table Toolbar */}
        <div className="p-6 md:p-8 border-b border-[#f1f5f9] flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <div className="relative w-full md:w-96 group">
              <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-[#94a3b8] group-focus-within:text-purple-500 transition-colors" />
              <input
                type="text"
                placeholder="Find reporter by name, email, or IINSAF ID..."
                className="w-full pl-12 pr-4 py-3.5 bg-[#f8fafc] border-transparent rounded-2xl outline-none focus:ring-4 focus:ring-purple-500/10 focus:bg-white focus:border-purple-200 transition-all text-sm font-medium text-[#334155]"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="flex items-center gap-4 text-xs font-bold text-[#94a3b8] uppercase tracking-widest">
            <span className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-500"></div> Online
            </span>
            <span className="text-[#e2e8f0]">|</span>
            <span>Refreshed {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
          </div>
        </div>

        {/* Enhanced Table Design */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-[#f8fafc]">
                <th className="px-6 py-4 text-left text-[10px] font-black text-[#64748b] uppercase tracking-widest border-b border-[#f1f5f9]">#</th>
                <th className="px-8 py-5 text-left text-xs font-black text-[#64748b] uppercase tracking-widest">Registry Agent</th>
                <th className="px-8 py-5 text-left text-xs font-black text-[#64748b] uppercase tracking-widest text-center">Identity</th>
                <th className="px-8 py-5 text-left text-xs font-black text-[#64748b] uppercase tracking-widest">Wallet</th>
                <th className="px-8 py-5 text-left text-xs font-black text-[#64748b] uppercase tracking-widest">Contact Info</th>
                <th className="px-8 py-5 text-left text-xs font-black text-[#64748b] uppercase tracking-widest">Geography</th>
                <th className="px-8 py-5 text-center text-xs font-black text-[#64748b] uppercase tracking-widest">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#f1f5f9]">
              {loading ? (
                Array(5).fill(0).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td colSpan="6" className="px-8 py-8"><div className="h-10 bg-gray-100 rounded-2xl w-full"></div></td>
                  </tr>
                ))
              ) : currentItems.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-8 py-20 text-center">
                    <div className="flex flex-col items-center gap-4">
                      <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center text-[#cbd5e1] text-4xl">
                        <FaSearch />
                      </div>
                      <p className="text-[#94a3b8] font-bold uppercase tracking-tight">No verified reporters found matching results</p>
                    </div>
                  </td>
                </tr>
              ) : (
                currentItems.map((reporter, idx) => (
                  <tr key={reporter._id} className="hover:bg-gray-50 transition-all duration-150">
                    <td className="px-6 py-3 whitespace-nowrap text-[10px] font-black text-gray-400">
                      {(currentPage - 1) * itemsPerPage + idx + 1}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          {reporter.image ? (
                            <img
                              src={reporter.image}
                              alt={reporter.name}
                              className="w-10 h-10 rounded-xl object-cover border-2 border-white shadow-md shadow-purple-500/10"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-700 flex items-center justify-center text-white text-sm font-black shadow-md shadow-purple-500/20">
                              {reporter.name?.charAt(0) || <FaIdCard />}
                            </div>
                          )}
                          <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-white border-2 border-white rounded-full flex items-center justify-center">
                            <div className="w-full h-full rounded-full bg-emerald-500"></div>
                          </div>
                        </div>
                        <div>
                          <p className="text-sm font-bold text-[#1e293b] uppercase tracking-tight">{reporter.name || "Unknown Agent"}</p>
                          <p className="text-[9px] text-[#94a3b8] font-black uppercase tracking-widest">{reporter.designation || "Press Representative"}</p>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-3 whitespace-nowrap text-center text-xs">
                      <div className="inline-flex flex-col items-center gap-1">
                        <span className="px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest bg-emerald-50 text-emerald-600 border border-emerald-100 flex items-center gap-1 shadow-sm">
                          Verified
                        </span>
                        <div className="text-gray-800 text-[10px] font-mono font-black">
                          {reporter.iinsafId || "LICENSE_PENDING"}
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-3 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 bg-emerald-50 text-emerald-600 rounded-lg flex items-center justify-center">
                          <FaWallet size={12} />
                        </div>
                        <p className="font-black text-[#1e293b]">₹{(reporter.walletBalance || 0).toFixed(2)}</p>
                      </div>
                    </td>

                    <td className="px-6 py-3 whitespace-nowrap text-xs">
                      <div className="flex flex-col">
                        <span className="font-bold text-[#475569]">{reporter.email || 'NO_EMAIL'}</span>
                        <span className="text-[10px] text-gray-400 font-medium">{reporter.mobileNo || 'NO_MOBILE'}</span>
                      </div>
                    </td>

                    <td className="px-6 py-3 whitespace-nowrap text-sm">
                      <div className="flex flex-col">
                        <span className="font-bold text-[#1e293b] uppercase tracking-tighter">{reporter.city || "Rest of India"}</span>
                        <span className="text-[9px] text-[#94a3b8] font-bold uppercase">{reporter.state || "National"}</span>
                      </div>
                    </td>

                    <td className="px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center justify-center gap-2">
                        {reporter.channelLinks && reporter.channelLinks.length > 0 && (
                          <div className="flex flex-wrap gap-1 mr-2">
                            {reporter.channelLinks.slice(0, 2).map((link, i) => (
                              <a
                                key={i}
                                href={link.link}
                                target="_blank"
                                className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded-md border border-blue-100 flex items-center gap-1 text-[7px] font-black uppercase hover:bg-blue-600 hover:text-white transition-all"
                                title={link.platform || "Visit Link"}
                              >
                                <FaLink size={6} /> {link.platform ? `${link.platform} Link` : "Platform Link"}
                              </a>
                            ))}
                          </div>
                        )}
                        <button
                          onClick={() => showDeleteConfirmation(reporter)}
                          className="w-8 h-8 flex items-center justify-center bg-red-50 text-red-500 hover:bg-red-500 hover:text-white rounded-lg transition-all border border-red-50"
                        >
                          <FaTrash size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Improved Pagination */}
        {totalPages > 1 && (
          <div className="p-8 bg-[#f8fafc] border-t border-[#f1f5f9] flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-[#64748b] font-bold">
              Archived <span className="text-[#1e293b]">{indexOfFirstItem + 1}</span> - <span className="text-[#1e293b]">{Math.min(indexOfLastItem, filteredCards.length)}</span> of <span className="text-[#1e293b] font-black">{filteredCards.length}</span> licensed agents
            </p>

            <div className="flex items-center gap-2 bg-white p-1.5 rounded-2xl border border-[#e2e8f0] shadow-sm">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="w-10 h-10 flex items-center justify-center text-[#64748b] hover:bg-gray-50 disabled:opacity-30 transition-all rounded-xl"
              >
                <FaChevronLeft size={12} />
              </button>

              <div className="flex items-center gap-1.5 px-2">
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`w-10 h-10 flex items-center justify-center rounded-xl text-xs font-black transition-all ${currentPage === i + 1
                      ? "bg-purple-600 text-white shadow-lg shadow-purple-500/20 scale-110"
                      : "text-gray-500 hover:bg-gray-50 hover:text-purple-600"
                      }`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="w-10 h-10 flex items-center justify-center text-[#64748b] hover:bg-gray-50 disabled:opacity-30 transition-all rounded-xl"
              >
                <FaChevronRight size={12} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal - Glassmorphism Design */}
      {showDeleteModal && reporterToDelete && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#0f172a]/80 backdrop-blur-xl animate-in fade-in duration-300">
          <div className="bg-white rounded-[3rem] shadow-2xl w-full max-w-md overflow-hidden border border-white/20 animate-in zoom-in-95 duration-300">
            <div className="p-10 text-center">
              <div className="w-24 h-24 bg-red-50 rounded-[2.5rem] flex items-center justify-center mx-auto mb-8 shadow-inner ring-4 ring-red-100">
                <FaExclamationTriangle className="w-10 h-10 text-red-500" />
              </div>
              <h3 className="text-3xl font-black text-[#1e293b] mb-4 uppercase tracking-tighter">Logout ID?</h3>
              <p className="text-sm text-[#64748b] font-medium leading-relaxed mb-10 px-4">
                You are about to revoke the press credentials for <span className="text-red-600 font-black">"{reporterToDelete.name}"</span>. This will invalidate their digital license across the network.
              </p>

              <div className="flex flex-col gap-3">
                <button
                  onClick={() => handleDeleteReporter(reporterToDelete._id)}
                  disabled={deleteLoading === reporterToDelete._id}
                  className="w-full py-4 bg-red-600 hover:bg-red-700 text-white font-black rounded-2xl text-sm transition-all shadow-xl shadow-red-500/30 flex items-center justify-center gap-3 active:scale-95 disabled:opacity-70"
                >
                  {deleteLoading === reporterToDelete._id ? (
                    <div className="w-5 h-5 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : <FaTrash />}
                  {deleteLoading === reporterToDelete._id ? "TERMINATING..." : "CONFIRM TERMINATION"}
                </button>
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="w-full py-4 bg-gray-50 hover:bg-gray-100 text-[#64748b] font-black rounded-2xl text-sm transition-all uppercase tracking-widest"
                >
                  Abort Operation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TotalReporter;